import React, { useEffect, useState, useRef } from 'react';
import { CheckIcon, ArrowRightIcon, ChevronRightIcon, SparklesIcon, ShieldCheckIcon, ClockIcon, UsersIcon, AwardIcon } from 'lucide-react';
import { Header } from './Header';
import { Footer } from './Footer';
export const MagicHomepage = () => {
  return <div className="flex flex-col min-h-screen w-full bg-white" data-id="element-3283">
      <style jsx global data-id="element-3284">{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        * {
          font-family:
            'Inter',
            -apple-system,
            BlinkMacSystemFont,
            sans-serif;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        .gradient-text {
          background: linear-gradient(135deg, #0b757a 0%, #1ddbdb 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      `}</style>
      <Header data-id="element-3285" />
      <main className="flex-grow" data-id="element-3286">
        <ModernHero data-id="element-3287" />
        <TrustIndicators data-id="element-3288" />
        <ValueProposition data-id="element-3289" />
        <FeaturesGrid data-id="element-3290" />
        <SocialProof data-id="element-3291" />
        <SimpleCTA data-id="element-3292" />
      </main>
      <Footer data-id="element-3293" />
    </div>;
};
const ModernHero = () => {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    setIsVisible(true);
  }, []);
  return <section className="relative pt-20 pb-32 md:pt-32 md:pb-40 overflow-hidden" data-id="element-3294">
      {/* Subtle background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-teal-50/30" data-id="element-3295"></div>
      {/* Decorative elements */}
      <div className="absolute top-20 right-0 w-96 h-96 bg-teal-100 rounded-full blur-3xl opacity-20" data-id="element-3296"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-aqua-100 rounded-full blur-3xl opacity-10" data-id="element-3297"></div>
      <div className="container mx-auto px-6 relative z-10" data-id="element-3298">
        <div className="max-w-4xl mx-auto text-center" data-id="element-3299">
          <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-3300">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 rounded-full mb-8 border border-teal-100" data-id="element-3301">
              <SparklesIcon className="w-4 h-4 text-teal-600" data-id="element-3302" />
              <span className="text-sm font-medium text-teal-900" data-id="element-3303">
                Trusted by 10,000+ professionals
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-gray-900 mb-6 leading-tight" data-id="element-3304">
              Professional training
              <br data-id="element-3305" />
              <span className="gradient-text" data-id="element-3306">made simple</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed" data-id="element-3307">
              Accredited compliance and skills training for organizations across
              Ireland, the UK, and Europe. Start training in minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16" data-id="element-3308">
              <a href="/explore" className="group px-8 py-4 bg-teal text-white font-semibold rounded-lg hover:bg-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center gap-2" data-id="element-3309">
                Get started
                <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-3310" />
              </a>
              <a href="/demo" className="px-8 py-4 bg-white text-gray-900 font-semibold rounded-lg border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all duration-300" data-id="element-3311">
                Book a demo
              </a>
            </div>
            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto pt-8 border-t border-gray-200" data-id="element-3312">
              <div data-id="element-3313">
                <div className="text-3xl font-bold text-gray-900 mb-1" data-id="element-3314">97%</div>
                <div className="text-sm text-gray-600" data-id="element-3315">Completion rate</div>
              </div>
              <div data-id="element-3316">
                <div className="text-3xl font-bold text-gray-900 mb-1" data-id="element-3317">12</div>
                <div className="text-sm text-gray-600" data-id="element-3318">Countries</div>
              </div>
              <div data-id="element-3319">
                <div className="text-3xl font-bold text-gray-900 mb-1" data-id="element-3320">
                  24/7
                </div>
                <div className="text-sm text-gray-600" data-id="element-3321">Access</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
const TrustIndicators = () => {
  return <section className="py-12 bg-white border-y border-gray-100" data-id="element-3322">
      <div className="container mx-auto px-6" data-id="element-3323">
        <div className="flex flex-wrap items-center justify-center gap-12" data-id="element-3324">
          <div className="flex items-center gap-3" data-id="element-3325">
            <ShieldCheckIcon className="w-6 h-6 text-teal" data-id="element-3326" />
            <span className="font-medium text-gray-900" data-id="element-3327">CPD Accredited</span>
          </div>
          <div className="flex items-center gap-3" data-id="element-3328">
            <AwardIcon className="w-6 h-6 text-teal" data-id="element-3329" />
            <span className="font-medium text-gray-900" data-id="element-3330">ISO Certified</span>
          </div>
          <div className="flex items-center gap-3" data-id="element-3331">
            <UsersIcon className="w-6 h-6 text-teal" data-id="element-3332" />
            <span className="font-medium text-gray-900" data-id="element-3333">10,000+ Users</span>
          </div>
        </div>
      </div>
    </section>;
};
const ValueProposition = () => {
  const cards = [{
    title: 'For individuals',
    description: 'Professional certification on your schedule. Complete courses online and receive instant certification.',
    icon: '👤'
  }, {
    title: 'For teams',
    description: 'Assign training, track progress, and manage compliance for your entire organization.',
    icon: '👥'
  }, {
    title: 'For enterprises',
    description: 'Custom branded academies with SSO, advanced analytics, and dedicated support.',
    icon: '🏢'
  }];
  return <section className="py-24 bg-gradient-to-b from-white to-gray-50" data-id="element-3334">
      <div className="container mx-auto px-6" data-id="element-3335">
        <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-3336">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-id="element-3337">
            Training that works for everyone
          </h2>
          <p className="text-xl text-gray-600" data-id="element-3338">
            Whether you're an individual, team, or enterprise - we have the
            right solution
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-3339">
          {cards.map((card, i) => <div key={i} className="group bg-white rounded-2xl p-8 border border-gray-200 hover:border-teal-200 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1" data-id="element-3340">
              <div className="text-4xl mb-4" data-id="element-3341">{card.icon}</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-3342">
                {card.title}
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-3343">
                {card.description}
              </p>
            </div>)}
        </div>
      </div>
    </section>;
};
const FeaturesGrid = () => {
  const features = [{
    icon: <ClockIcon className="w-6 h-6" data-id="element-3344" />,
    title: 'Instant access',
    description: 'Start training immediately with no setup required'
  }, {
    icon: <ShieldCheckIcon className="w-6 h-6" data-id="element-3345" />,
    title: 'Compliance ready',
    description: 'Meets EU and UK regulatory standards'
  }, {
    icon: <AwardIcon className="w-6 h-6" data-id="element-3346" />,
    title: 'Instant certification',
    description: 'Certificates issued upon completion'
  }, {
    icon: <UsersIcon className="w-6 h-6" data-id="element-3347" />,
    title: 'Team management',
    description: 'Track progress across your organization'
  }];
  return <section className="py-24 bg-white" data-id="element-3348">
      <div className="container mx-auto px-6" data-id="element-3349">
        <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-3350">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-id="element-3351">
            Everything you need
          </h2>
          <p className="text-xl text-gray-600" data-id="element-3352">
            A complete training platform designed for modern organizations
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto" data-id="element-3353">
          {features.map((feature, i) => <div key={i} className="text-center" data-id="element-3354">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-teal-50 text-teal rounded-xl mb-4" data-id="element-3355">
                {feature.icon}
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-3356">
                {feature.title}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed" data-id="element-3357">
                {feature.description}
              </p>
            </div>)}
        </div>
      </div>
    </section>;
};
const SocialProof = () => {
  const testimonials = [{
    quote: 'The platform transformed how we approach compliance training. Our completion rates increased significantly.',
    author: 'Sarah Johnson',
    role: 'HR Director, TechCorp Ireland'
  }, {
    quote: 'Simple to deploy across our organization. The management dashboard makes tracking straightforward.',
    author: 'Michael Chen',
    role: 'Operations Manager, RetailPro UK'
  }, {
    quote: 'The accreditations meet our compliance requirements. Our auditors accept the certificates without question.',
    author: 'Emma Richards',
    role: 'Compliance Officer, FinanceHub Europe'
  }];
  return <section className="py-24 bg-gradient-to-b from-gray-50 to-white" data-id="element-3358">
      <div className="container mx-auto px-6" data-id="element-3359">
        <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-3360">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-id="element-3361">
            Trusted by organizations worldwide
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-3362">
          {testimonials.map((testimonial, i) => <div key={i} className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm" data-id="element-3363">
              <p className="text-gray-700 mb-6 leading-relaxed" data-id="element-3364">
                "{testimonial.quote}"
              </p>
              <div data-id="element-3365">
                <div className="font-semibold text-gray-900" data-id="element-3366">
                  {testimonial.author}
                </div>
                <div className="text-sm text-gray-600" data-id="element-3367">{testimonial.role}</div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
};
const SimpleCTA = () => {
  return <section className="py-24 bg-gradient-to-br from-teal to-teal-700 relative overflow-hidden" data-id="element-3368">
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0di00aC0ydjRoLTR2Mmg0djRoMnYtNGg0di0yaC00em0wLTMwVjBoLTJ2NGgtNHY0aDJ2LTRoNHYtMkg2ek02IDM0di00SDR2NGgwdjJoNHY0aDJ2LTRoNHYtMkg2ek02IDRWMEg0djRIMHYyaDR2NGgyVjZoNFY0aC00ek02IDRWMEg0djRIMHYyaDR2NGgyVjZoNFY0SDZ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-10" data-id="element-3369"></div>
      <div className="container mx-auto px-6 relative z-10" data-id="element-3370">
        <div className="max-w-4xl mx-auto text-center" data-id="element-3371">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-3372">
            Ready to get started?
          </h2>
          <p className="text-xl text-white/90 mb-12" data-id="element-3373">
            Join thousands of organizations using Kivarro for professional
            training
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-3374">
            <a href="/explore" className="px-8 py-4 bg-white text-teal font-semibold rounded-lg hover:bg-gray-50 transition-all duration-300 shadow-xl" data-id="element-3375">
              Start free trial
            </a>
            <a href="/demo" className="px-8 py-4 bg-transparent text-white font-semibold rounded-lg border-2 border-white hover:bg-white/10 transition-all duration-300" data-id="element-3376">
              Book a demo
            </a>
          </div>
        </div>
      </div>
    </section>;
};